// File: src/config/roles.ts
/**
 * Role catalog and convenience groups for the UI.
 * Backend remains the source of truth; these slugs must match Django.
 *
 * @fileoverview Centralized role definitions for type safety and maintainability
 * @version 1.0.1
 */

export const ROLES = {
    PLATFORM: {
        ADMIN: 'platform_admin',
        ENGINEER: 'platform_engineer',
        OPERATOR: 'platform_operator',
        AUDITOR: 'platform_auditor',
        OBSERVER: 'platform_observer'
    },
    TENANT: {
        ADMIN: 'tenant_admin',
        DOMAIN_GROUP_OWNER: 'tenant_domain_group_owner',
        DOMAIN_OWNER: 'tenant_domain_owner',
        REPORTER: 'tenant_reporter',
        VIEWER: 'tenant_viewer',
        MEMBER: 'tenant_member'
    },
    ADDITIVE: {
        PLATFORM_LEAD: 'platform_lead',
        PLATFORM_PREVIEWER: 'platform_previewer'
    }
} as const

export type RoleSlug =
    | (typeof ROLES.PLATFORM)[keyof typeof ROLES.PLATFORM]
    | (typeof ROLES.TENANT)[keyof typeof ROLES.TENANT]
    | (typeof ROLES.ADDITIVE)[keyof typeof ROLES.ADDITIVE]

export const ROLE_LABELS: Record<RoleSlug, string> = {
    platform_admin: 'Platform Administrator',
    platform_engineer: 'Platform Engineer',
    platform_operator: 'Platform Operator',
    platform_auditor: 'Platform Auditor',
    platform_observer: 'Platform Observer',
    platform_lead: 'Platform Lead (Elevated)',
    platform_previewer: 'Platform Previewer',
    tenant_admin: 'Organization Administrator',
    tenant_domain_group_owner: 'Domain Group Owner',
    tenant_domain_owner: 'Domain Owner',
    tenant_reporter: 'Reporter',
    tenant_viewer: 'Viewer',
    tenant_member: 'Member'
} as const

/**
 * Keep groups factual (constituencies). Prefer backend perms for capability gating.
 */
export const ROLE_GROUPS = {
    PLATFORM_STAFF: [
        ROLES.PLATFORM.ADMIN,
        ROLES.PLATFORM.ENGINEER,
        ROLES.PLATFORM.OPERATOR,
        ROLES.PLATFORM.AUDITOR,
        ROLES.PLATFORM.OBSERVER
    ],
    TENANT_ADMINISTRATORS: [
        ROLES.TENANT.ADMIN,
        ROLES.TENANT.DOMAIN_GROUP_OWNER,
        ROLES.TENANT.DOMAIN_OWNER
    ],
    READONLY_ROLES: [
        ROLES.PLATFORM.AUDITOR,
        ROLES.PLATFORM.OBSERVER,
        ROLES.TENANT.REPORTER,
        ROLES.TENANT.VIEWER
    ]
    // If we truly need these later, we can uncomment and document policy:
    // PLATFORM_CONFIGURERS: [ROLES.PLATFORM.ADMIN, ROLES.PLATFORM.OPERATOR],
    // PLATFORM_ELEVATED: [ROLES.PLATFORM.ADMIN, ROLES.PLATFORM.ENGINEER],
    // ORGANIZATION_MANAGERS: [ROLES.PLATFORM.ADMIN, ROLES.PLATFORM.OPERATOR, ROLES.TENANT.ADMIN],
    // CROSS_TENANT_VIEWERS: [ROLES.PLATFORM.ADMIN, ROLES.PLATFORM.ENGINEER, ROLES.PLATFORM.AUDITOR],
} as const

export type RoleGroupKey = keyof typeof ROLE_GROUPS

/** Precomputed set for O(1) validation checks */
export const ALL_ROLE_SLUGS = [
    ...Object.values(ROLES.PLATFORM),
    ...Object.values(ROLES.TENANT),
    ...Object.values(ROLES.ADDITIVE)
] as const
const ALL_ROLE_SLUGS_SET = new Set<string>(ALL_ROLE_SLUGS as readonly string[])

export const roleSet = (roles: readonly RoleSlug[] = []) => {
    const set = new Set<RoleSlug>(roles)
    return {
        hasAny: (...needles: RoleSlug[]) => needles.some((r) => set.has(r)),
        hasAll: (...needles: RoleSlug[]) => needles.every((r) => set.has(r)),
        inGroup: (group: RoleGroupKey) => ROLE_GROUPS[group].some((r) => set.has(r)),
        groupIntersection: (group: RoleGroupKey) => ROLE_GROUPS[group].filter((r) => set.has(r)),
        hasExactlyOne: (...needles: RoleSlug[]) => needles.filter((r) => set.has(r)).length === 1
    }
}

/** Runtime guard */
export function isValidRoleSlug(value: unknown): value is RoleSlug {
    return typeof value === 'string' && ALL_ROLE_SLUGS_SET.has(value)
}

/** Defensive sanitizer */
export function validateRoles(roles: unknown[] | null | undefined): RoleSlug[] {
    if (!Array.isArray(roles)) return []
    return roles.filter(isValidRoleSlug)
}
